import { GridColDef } from "@mui/x-data-grid-pro";
import {
  CopyIcon,
  GKDataGrid,
  Grid2,
  IconButton,
  LinkIcon,
  NoData,
  SendIcon,
  Show,
  StatusField,
  Tooltip,
  Typography,
} from "components";
import dayjs from "dayjs";
import useGenerateQueryParams from "hooks/useGenerateQueryParams";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { useState } from "react";
import { PAYMENT_LINK_LIST } from "utils/apiRoutes";
import axiosInstance from "utils/axiosInstance";
import {
  copyTextToClipboard,
  extraColumnsField,
  matchDate,
} from "utils/helpers";
import { moneyCommaSeparator } from "utils/MathFunction";
import useDocumentTitle from "utils/useDocumentTitle";
import PaymentStatusModal from "./PaymentStatusModal";

const PaymentLinkList = () => {
  useDocumentTitle("Payment Link");

  const [dateModal, setDateModal] = useState(false);
  const [paymentData, setPaymentData] = useState({});

  const requestBody = useGenerateQueryParams("paymentLink");

  const { data, isFetching } = useFetchQuery({
    key: ["PAYMENT_LINK_LIST", requestBody],
    route: PAYMENT_LINK_LIST,
    requestBody,
  });

  const { mutate: resendPaymentLink } = useMutation({
    mutationKey: ["RESEND_PAYMENT_GATEWAY_LINK"],
    mutationFn: (id: string) =>
      axiosInstance.post(`/razorpay-payment-link/${id}/resend/`),
  });

  const columns: GridColDef[] = [
    { ...extraColumnsField("Invoice No.", "invoice", 150, 1) },
    {
      ...extraColumnsField("Date", "invoiceDate", 150, 1),
      valueFormatter: (value) => matchDate(value?.value),
      valueGetter: (params) =>
        dayjs(params?.row?.invoiceDate).format("D MMM YYYY"),
    },
    {
      ...extraColumnsField("Portfolios", "portfolioName", 250, 1),
      renderCell: (params) => {
        return (
          <Grid2
            container
            display={"flex"}
            flexDirection={"column"}
            alignItems={"flex-start"}
          >
            <Typography fontWeight={700}>{params?.row?.clientName}</Typography>
            <Typography fontSize={12}>
              {params?.row?.portfolioName} - {params?.row?.portfolioCode}
            </Typography>
          </Grid2>
        );
      },
    },
    {
      ...extraColumnsField("Amount", "gettingAmount", 150, 1),
      type: "number",
      valueGetter: (row) => row?.row?.gettingAmount,
      valueFormatter: (params) => moneyCommaSeparator(params.value || 0),
    },
    {
      ...extraColumnsField("Payment Status", "paymentLinkStatus", 150, 1),
      renderCell: (params) => (
        <StatusField statusString={params?.row?.paymentLinkStatus} />
      ),
    },
    {
      ...extraColumnsField("Actions", "action", 150, 1),
      align: "right",
      headerAlign: "right",
      sortable: false,
      renderCell: (params) => (
        <Grid2 container gap={1} display={"flex"} justifyContent={"flex-end"}>
          <Tooltip title="Copy Payment Link">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                copyTextToClipboard(params.row.paymentLinkUrl);
              }}
            >
              <CopyIcon width={20} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Resend Payment Link">
            <IconButton
              disabled={params?.row?.paymentLinkStatus === "paid"}
              onClick={(event) => {
                event.stopPropagation();
                resendPaymentLink(params?.row?.id);
              }}
            >
              <SendIcon width={20} height={20} />
            </IconButton>
          </Tooltip>
        </Grid2>
      ),
    },
  ];

  return (
    <>
      <GKDataGrid
        loading={isFetching}
        columns={columns}
        rows={data?.data?.data || []}
        onRowClick={(params) => {
          setPaymentData(params.row);
          setDateModal(true);
        }}
        maxHeight={"calc(100vh - 306px)"}
        totalCount={data?.data?.totalCount || 0}
        name={"paymentLink"}
        tableName="payment_link"
        emptyComponent={
          <NoData
            text="No Payment Links Sent Yet"
            subText="Once you send a payment link to a client, it will appear here with its status."
            customImg={
              <LinkIcon height={80} width={80} color="var(--neutral-100)" />
            }
          />
        }
      />

      <Show.When isTrue={dateModal}>
        <PaymentStatusModal
          open={dateModal}
          setOpen={setDateModal}
          paymentData={paymentData}
        />
      </Show.When>
    </>
  );
};

export default PaymentLinkList;
